/**
 * Tauchgang erfassen und bearbeiten.
 *
 * Die Ansicht zeichnet nur — gerechnet, geprüft und geschrieben wird in
 * `formular.js`, das ohne DOM auskommt und deshalb geprüft ist.
 */

import { SPEICHER, alleAus, alleTauchgaenge } from '../db.js';
import { FELDER, GRUPPEN } from '../felder.js';
import { naechsteNummer, pruefe, speichereFormular, vergebeneNummern,
  zuFormular } from '../formular.js';
import { loesche } from '../saetze.js';
import { geraeteKennung } from '../sync.js';
import { eingabefeld } from './feldeingabe.js';

function ausDemFormular(wurzel) {
  const werte = {};
  for (const f of FELDER) {
    werte[f.schluessel] = wurzel.querySelector(`[name="${f.schluessel}"]`)?.value ?? '';
  }
  // Kein Feld des Tauchgangs, sondern eine Frage an den Benutzer: Ist der
  // Pruefhinweis erledigt? Steht das Haekchen nicht da, bleibt es unbeantwortet
  // und `nimmtPruefmarke` entscheidet, dass nichts zu tun ist.
  const haken = wurzel.querySelector('[name="pruefung_erledigt"]');
  if (haken) werte.pruefung_erledigt = haken.checked;
  return werte;
}

function zeigeMeldungen(wurzel, { fehler, hinweise }) {
  for (const feldHuelle of wurzel.querySelectorAll('.feld')) {
    feldHuelle.classList.remove('hat-fehler', 'hat-hinweis');
    feldHuelle.querySelector('.feldmeldung').textContent = '';
  }
  const setze = (eintrag, klasse) => {
    const eingabe = wurzel.querySelector(`[name="${eintrag.feld}"]`);
    const huelle = eingabe?.closest('.feld');
    if (!huelle) return;
    huelle.classList.add(klasse);
    huelle.querySelector('.feldmeldung').textContent = eintrag.text;
  };
  for (const h of hinweise) setze(h, 'hat-hinweis');
  for (const f of fehler) setze(f, 'hat-fehler');
}

/**
 * Löschen — endgültig, deshalb mit einer Rückfrage, die den Tauchgang beim
 * Namen nennt statt "Sind Sie sicher?".
 */
function loeschknopf(db, wurzel, tg, meldung, aufFertig) {
  const knopf = document.createElement('button');
  knopf.type = 'button';
  knopf.className = 'knopf loeschen';
  knopf.textContent = 'Löschen';
  knopf.addEventListener('click', async () => {
    const wann = String(tg.start_utc ?? '').slice(0, 10);
    const wo = tg.site_name ?? 'ohne Platz';
    const tief = typeof tg.max_depth_m === 'number' ? `, ${tg.max_depth_m} m` : '';
    const frage = `Tauchgang ${tg.dive_number ?? ''} vom ${wann}, ${wo}${tief} `
      + '— endgültig löschen?';
    if (!globalThis.confirm(frage)) return;
    try {
      await loesche(db, SPEICHER.dives, [tg.id]);
      wurzel.dataset.sauber = 'ja';
      aufFertig(null);
    } catch (fehler) {
      meldung.textContent = `Nicht gelöscht: ${fehler.message}`;
      meldung.classList.add('fehler');
    }
  });
  return knopf;
}

function fussleiste(db, wurzel, tg, alle, aufFertig) {
  const leiste = document.createElement('div');
  leiste.className = 'fussleiste';
  leiste.innerHTML = `<button type="submit" class="knopf">Speichern</button>
    <span class="meldung" role="status" aria-live="polite"></span>`;
  const meldung = leiste.querySelector('.meldung');

  // Prüfen beim Tippen — der Hinweis erscheint, wo er hingehört, und nicht
  // erst als Sammelmeldung beim Speichern.
  wurzel.addEventListener('input', () => {
    wurzel.dataset.sauber = 'nein';
    zeigeMeldungen(wurzel, pruefe(ausDemFormular(wurzel), vergebeneNummern(alle, tg?.id)));
  });

  wurzel.addEventListener('submit', async (ereignis) => {
    ereignis.preventDefault();
    const werte = ausDemFormular(wurzel);
    const ergebnis = pruefe(werte, vergebeneNummern(alle, tg?.id));
    zeigeMeldungen(wurzel, ergebnis);
    if (ergebnis.fehler.length) {
      meldung.textContent = 'Bitte die rot markierten Angaben ergänzen.';
      return;
    }
    try {
      meldung.textContent = 'Speichert …';
      const { id, neuerPlatz, neueReise } = await speichereFormular(db, werte, tg,
        { geraet: geraeteKennung() });
      const dazu = [neuerPlatz && 'Platz angelegt', neueReise && 'Reise angelegt']
        .filter(Boolean);
      meldung.textContent = dazu.length ? `Gespeichert (${dazu.join(', ')}).` : 'Gespeichert.';
      wurzel.dataset.sauber = 'ja';
      aufFertig(id);
    } catch (fehler) {
      meldung.textContent = `Nicht gespeichert: ${fehler.message}`;
      meldung.classList.add('fehler');
    }
  });

  if (tg) leiste.append(loeschknopf(db, wurzel, tg, meldung, aufFertig));
  return leiste;
}

/**
 * Wer das Formular mit ungespeicherten Änderungen verlässt, wird gefragt.
 *
 * Die App ist ein Hash-Router: Ein Klick auf "Liste" löst kein Neuladen aus,
 * deshalb greift `beforeunload` nicht. Bewacht wird der Wechsel der Adresse.
 * Nach dem Speichern setzt die Fussleiste die Marke zurück, sonst fragte die
 * App auch dann, wenn alles gesichert ist.
 */
function bewacheVerlassen(wurzel, zurueck) {
  const beiWechsel = () => {
    if (wurzel.dataset.sauber !== 'nein') return;
    if (globalThis.confirm('Ungespeicherte Änderungen verwerfen?')) {
      wurzel.dataset.sauber = 'ja';
      return;
    }
    wurzel.dataset.sauber = 'nein';
    location.hash = zurueck;
  };
  globalThis.addEventListener('hashchange', beiWechsel);
  // main.js ruft aufraeumen(), sobald die Ansicht ersetzt wird.
  wurzel.aufraeumen = () => globalThis.removeEventListener('hashchange', beiWechsel);
}

async function aufbauen(db, wurzel, tg, werte, aufFertig) {
  const alle = await alleTauchgaenge(db);
  const plaetze = await alleAus(db, SPEICHER.sites);
  const reisen = await alleAus(db, SPEICHER.trips);

  if (!tg) werte.dive_number = naechsteNummer(alle);

  const vorschlaege = {
    site_name: plaetze.map((p) => p.name).filter(Boolean).sort(),
    trip_name: reisen.map((r) => r.name).filter(Boolean).sort(),
    buddies: [...new Set(alle.flatMap((t) => t.buddies ?? []))].sort(),
    weather: [...new Set(alle.map((t) => t.weather).filter(Boolean))].sort(),
  };

  for (const gruppe of GRUPPEN) {
    const felder = FELDER.filter((f) => f.gruppe === gruppe.schluessel);
    if (felder.length === 0) continue;

    let behaelter;
    if (gruppe.schluessel === 'kern') {
      behaelter = document.createElement('section');
      behaelter.className = 'gruppe';
    } else {
      // Alles jenseits des Kerns bleibt zugeklappt: Der Normalfall soll in
      // einem Bildschirm erfassbar sein.
      behaelter = document.createElement('details');
      behaelter.className = 'gruppe';
      const titel = document.createElement('summary');
      titel.textContent = gruppe.titel;
      behaelter.append(titel);
    }
    for (const f of felder) {
      behaelter.append(eingabefeld(f, werte[f.schluessel], vorschlaege[f.schluessel]));
    }
    wurzel.append(behaelter);
  }

  // Der Pruefhinweis gehoert dorthin, wo man ihn abstellen kann. Er stand nur
  // auf der Detailseite; wer den bemaengelten Wert im Formular korrigierte,
  // sah weder den Grund noch wurde die Marke los.
  if (tg?.needs_review) {
    const hinweis = document.createElement('div');
    hinweis.className = 'pruefhinweis';
    const text = document.createElement('p');
    text.className = 'pruefhinweis-text';
    text.textContent = `Zu prüfen: ${tg.review_note ?? ''}`.trim();
    const schalter = document.createElement('label');
    schalter.className = 'schalter pruefhinweis-schalter';
    schalter.innerHTML = '<input type="checkbox" name="pruefung_erledigt" checked>'
      + '<span>erledigt</span>';
    hinweis.append(text, schalter);
    wurzel.prepend(hinweis);
  }

  if ((tg?.tanks?.length ?? 0) > 1) {
    const hinweis = document.createElement('p');
    hinweis.className = 'hinweis';
    hinweis.textContent = `Dieser Tauchgang hat ${tg.tanks.length} Flaschen. `
      + 'Bearbeitet wird die erste, die übrigen bleiben unverändert.';
    wurzel.append(hinweis);
  }

  if (tg && tg.source && tg.source !== 'app') {
    const herkunft = document.createElement('p');
    herkunft.className = 'hinweis';
    herkunft.textContent = `Messwerte stammen aus ${tg.source} — Änderungen `
      + 'überschreiben, was der Tauchcomputer aufgezeichnet hat.';
    wurzel.append(herkunft);
  }

  wurzel.append(fussleiste(db, wurzel, tg, alle, aufFertig));
  bewacheVerlassen(wurzel, tg ? `#/bearbeiten/${tg.id}` : '#/neu');
  wurzel.dataset.stand = 'bereit';
  return wurzel;
}

export function ansichtFormular(db, tg, aufFertig) {
  const wurzel = document.createElement('form');
  wurzel.className = 'formular';
  wurzel.noValidate = true;
  wurzel.dataset.sauber = 'ja';

  const werte = zuFormular(tg);
  const kopf = document.createElement('header');
  kopf.innerHTML = `<a class="zurueck" href="${tg ? `#/tauchgang/${tg.id}` : '#/'}">← Zurück</a>
    <h1>${tg ? `Tauchgang ${tg.dive_number ?? ''} bearbeiten` : 'Neuer Tauchgang'}</h1>`;
  wurzel.append(kopf);

  return aufbauen(db, wurzel, tg, werte, aufFertig);
}
